import { useCallback, useEffect, useRef, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';

import { Plan } from '../../Dashboard.types';
import { useApplicationState } from '../../hooks/useApplicationState';
import { usePaymentPlansData } from '../../hooks/usePaymentPlansData';
import { AccountApplicationSection } from '../AccountApplicationSection/AccountApplicationSection';
import { AccountPlansSection } from '../AccountPlansSection/AccountPlansSection';

/**
 * Thin wrapper inside the Suspense boundary that owns `selectedPlan` state.
 * Lives here so the initial value can be computed synchronously from the
 * data returned by the suspense hooks — no effect-based "sync to parent"
 * and therefore no flash of empty content.
 *
 * Also the single call-site for useApplicationState / usePaymentPlansData
 * so that useSuspenseApplicationProcessor only fires once and the derived
 * computations aren't duplicated across children.
 */
export function AccountSections({
  isMobile,
  setIsSpendingLimitDrawerOpen,
  loadingRefreshSpendingLimit,
  handleRefreshSpendingLimit,
}: {
  isMobile: boolean;
  setIsSpendingLimitDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  loadingRefreshSpendingLimit: boolean;
  handleRefreshSpendingLimit: () => Promise<void>;
}) {
  const application = useApplicationState();
  const {
    ongoingPaymentPlans,
    capturedPlans,
    cancelledPayments,
    endedPayments,
  } = usePaymentPlansData();

  const trackPlanView = useCallback((plan: Plan | null) => {
    if (plan?.status) {
      tracking.ampli.buyerDashboardViewed({
        buyer_dashboard_state: plan.status,
      });
    }
  }, []);

  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(() => {
    const initialPlan =
      application ?? capturedPlans[0] ?? ongoingPaymentPlans[0] ?? null;

    trackPlanView(initialPlan);

    return initialPlan;
  });

  // When application processing completes and no plan was previously selected,
  // auto-select the application as the active plan.
  // Auto-select the application when it becomes available after async processing
  const hasAutoSelectedApplication = useRef(false);
  /* eslint-disable react-you-might-not-need-an-effect/no-event-handler, react-you-might-not-need-an-effect/no-chain-state-updates, react-hooks/set-state-in-effect -- syncing state after async application processing, not an event handler */
  useEffect(() => {
    if (hasAutoSelectedApplication.current) return;
    if (!application) return;
    if (selectedPlan) return;

    hasAutoSelectedApplication.current = true;
    setSelectedPlan(application);
    trackPlanView(application);
  }, [application, selectedPlan, trackPlanView]);
  /* eslint-enable react-you-might-not-need-an-effect/no-event-handler, react-you-might-not-need-an-effect/no-chain-state-updates, react-hooks/set-state-in-effect */

  const selectPaymentPlan = useCallback(
    (plan: Plan) => {
      setSelectedPlan(plan);
      trackPlanView(plan);
    },
    [trackPlanView],
  );

  return (
    <>
      <AccountApplicationSection
        application={application}
        selectedPlan={selectedPlan}
        selectPaymentPlan={selectPaymentPlan}
      />
      <AccountPlansSection
        isMobile={isMobile}
        application={application}
        ongoingPaymentPlans={ongoingPaymentPlans}
        capturedPlans={capturedPlans}
        cancelledPayments={cancelledPayments}
        endedPayments={endedPayments}
        selectedPlan={selectedPlan}
        selectPaymentPlan={selectPaymentPlan}
        setIsSpendingLimitDrawerOpen={setIsSpendingLimitDrawerOpen}
        loadingRefreshSpendingLimit={loadingRefreshSpendingLimit}
        handleRefreshSpendingLimit={handleRefreshSpendingLimit}
      />
    </>
  );
}
